import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {
    clampPan,
    FIT_VIEW,
    Point,
    refit,
    Size,
    snapToFit,
    wheelZoomFactor,
    ZoomBounds,
    zoomAt,
    zoomBounds,
    ZoomPan,
} from '../../domain/zoomPan';

/** After the last wheel event this long, zoom changes animate again. */
const WHEEL_IDLE_MS = 150;
/** After the stage last changed size this long, zoom changes animate again. */
const RESIZE_IDLE_MS = 150;

export interface ZoomPanControl {
    /** The current zoom, kept within `bounds`. */
    view: ZoomPan;
    bounds: ZoomBounds;
    zoomed: boolean;
    /** The wheel is zooming: changes follow it without a transition. */
    wheeling: boolean;
    /**
     * The stage was just resized (e.g. a side panel opened): the picture's box takes its new fit at
     * once, so its zoom must follow without a transition too, or it would pass through other sizes.
     */
    resizing: boolean;
    /** Attach to the stage: it is measured, and the wheel zooms over it. */
    stageRef: (node: HTMLElement | null) => void;
    /** A pointer position as a point relative to the stage's centre. */
    toStagePoint: (clientX: number, clientY: number) => Point;
    /** The zoom as last set, which may be ahead of the rendered `view` while events arrive. */
    latest: () => ZoomPan;
    set: (view: ZoomPan) => void;
    /** Zooms by `factor` about `anchor` (default: the stage's centre). */
    zoomBy: (factor: number, anchor?: Point) => void;
    reset: () => void;
    /** Ends a pinch or wheel zoom: one that stopped barely above fit goes back to it. */
    settle: () => void;
}

const NO_SIZE: Size = {width: 0, height: 0};

/**
 * Zoom and pan of a picture of size `image` (null: unknown) fitted into a stage element. A new
 * `resetKey` (the next photo) starts at fit.
 */
export function useZoomPan(image: Size | null, resetKey: string): ZoomPanControl {
    const [stageNode, setStageNode] = useState<HTMLElement | null>(null);
    const [stage, setStage] = useState<Size>(NO_SIZE);
    // Kept with the key and the fit size it was set for: `scale` is relative to that fit.
    const [state, setState] = useState<{ key: string; view: ZoomPan; fit: Size }>(
        {key: resetKey, view: FIT_VIEW, fit: NO_SIZE}
    );
    const [wheeling, setWheeling] = useState(false);
    const [resizing, setResizing] = useState(false);

    useLayoutEffect(() => {
        if (!stageNode) return;
        let measured: Size | undefined;
        let idleTimer: number | undefined;
        const measure = () => {
            // The layout size: a bounding rect would include the dialog's opening scale animation.
            const width = stageNode.clientWidth;
            const height = stageNode.clientHeight;
            if (measured?.width === width && measured.height === height) return;
            const first = !measured;
            measured = {width, height};
            setStage(measured);
            if (first) return;
            setResizing(true);
            window.clearTimeout(idleTimer);
            idleTimer = window.setTimeout(() => setResizing(false), RESIZE_IDLE_MS);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(stageNode);
        return () => {
            observer.disconnect();
            window.clearTimeout(idleTimer);
        };
    }, [stageNode]);

    const bounds = zoomBounds(image, stage);
    // A new fit (a resized stage, a panel opened beside it) keeps the zoomed picture's size on screen.
    const rawView = state.key === resetKey ? refit(state.view, state.fit, bounds) : FIT_VIEW;
    // Re-clamped on every render, so a resized stage never leaves the picture off its bounds.
    const view = clampPan(rawView, bounds);
    // For the wheel listener and the gestures, which run between renders: several events may
    // arrive before the next render, so each update starts from the latest one, not the rendered one.
    const boundsRef = useRef(bounds);
    boundsRef.current = bounds;
    const latestRef = useRef(view);
    latestRef.current = view;
    const keyRef = useRef(resetKey);
    keyRef.current = resetKey;

    function update(next: (current: ZoomPan) => ZoomPan) {
        const view = clampPan(next(clampPan(latestRef.current, boundsRef.current)), boundsRef.current);
        latestRef.current = view;
        setState({key: keyRef.current, view, fit: boundsRef.current.fit});
    }

    function toStagePoint(clientX: number, clientY: number): Point {
        const rect = stageNode?.getBoundingClientRect();
        if (!rect) return {x: 0, y: 0};
        return {x: clientX - rect.left - rect.width / 2, y: clientY - rect.top - rect.height / 2};
    }

    useEffect(() => {
        if (!stageNode) return;
        let idleTimer: number | undefined;
        const onWheel = (e: WheelEvent) => {
            // Not passive: the page must not scroll (nor the browser zoom on a trackpad pinch).
            e.preventDefault();
            const rect = stageNode.getBoundingClientRect();
            const anchor = {x: e.clientX - rect.left - rect.width / 2, y: e.clientY - rect.top - rect.height / 2};
            const factor = wheelZoomFactor(e.deltaY, e.deltaMode, e.ctrlKey);
            update((current) => zoomAt(current, current.scale * factor, anchor, boundsRef.current));
            setWheeling(true);
            window.clearTimeout(idleTimer);
            idleTimer = window.setTimeout(() => {
                setWheeling(false);
                update(snapToFit);
            }, WHEEL_IDLE_MS);
        };
        stageNode.addEventListener('wheel', onWheel, {passive: false});
        return () => {
            stageNode.removeEventListener('wheel', onWheel);
            window.clearTimeout(idleTimer);
        };
    }, [stageNode]);

    return {
        view,
        bounds,
        zoomed: view.scale > 1,
        wheeling,
        resizing,
        stageRef: setStageNode,
        toStagePoint,
        latest: () => latestRef.current,
        set: (next) => update(() => next),
        zoomBy: (factor, anchor = {x: 0, y: 0}) => update((current) => zoomAt(current, current.scale * factor, anchor, boundsRef.current)),
        reset: () => update(() => FIT_VIEW),
        settle: () => update(snapToFit),
    };
}
